import {createContext, useContext} from 'react';

import type {EagColumnState} from '../types/EagColumnState';
import type {Filters} from '../types/Filters';

export type ColumnMenuContextValue = {
  /** Whether the column header menu feature is enabled at all */
  showColumnMenu: boolean;
  /** Whether column pinning actions are shown in the menu */
  showPinning: boolean;
  /** Whether column autosize actions are shown in the menu */
  showAutosize: boolean;
  /** Current committed filter values (read-only view for the filter tab) */
  filterState: Filters;
  /** Read-only map of column states — used to read sort direction per column in the menu */
  columnStates: Map<string, EagColumnState>;
  onFilterChange: (key: string, value: unknown) => void;
  onPinColumn: (key: string, pinned: 'LEFT' | 'RIGHT' | null) => void;
  onAutosizeColumn: (key: string) => void;
  onAutosizeAllColumns: () => void;
  onCopyColumn: (key: string) => void;
  onResetColumns: () => void;
  onResetFilters: () => void;
  /** Set sort direction directly for a column (not toggle — Options tab sets exact value) */
  onSort: (key: string, sort: 'ASC' | 'DESC' | null) => void;
  /**
   * The grid's table-area container element — passed to the header menu Dropdown as
   * its floating-ui boundary so the menu shifts to stay horizontally inside the grid
   * (a bottom-end menu on the first column would otherwise escape over whatever sits
   * left of the grid, e.g. the DMS nav sidebar). Null/absent before mount → default
   * viewport clamping.
   */
  menuBoundary?: HTMLElement | null;
};

export const ColumnMenuContext = createContext<ColumnMenuContextValue | null>(null);

/**
 * Returns the column-menu API. Throws if used outside ColumnMenuContext.Provider.
 */
export function useColumnMenuContext(): ColumnMenuContextValue {
  const ctx = useContext(ColumnMenuContext);
  if (ctx === null) {
    throw new Error('useColumnMenuContext must be used inside ColumnMenuContext.Provider');
  }
  return ctx;
}
